import { useState, useRef, useCallback, useEffect } from 'react';
import {
  useTextToSpeechMutation,
  useSpeechToTextMutation,
} from '../store/api';

export type VoiceState = 'idle' | 'recording' | 'transcribing' | 'speaking';

interface UseVoiceChatOptions {
  /** Automatically speak agent responses when voice mode is on */
  autoSpeak?: boolean;
  /** TTS voice name (OpenAI fallback only) */
  voice?: string;
  /** TTS model (OpenAI fallback only) */
  model?: string;
  /** Called with transcribed text when STT completes */
  onTranscript?: (text: string) => void;
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface BrowserSpeechRecognitionEvent {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
}

export function useVoiceChat(options: UseVoiceChatOptions = {}) {
  const {
    autoSpeak = true,
    voice = 'nova',
    model = 'tts-1',
    onTranscript,
  } = options;

  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const [tts] = useTextToSpeechMutation();
  const [stt] = useSpeechToTextMutation();

  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.addEventListener('ended', () => {
      setVoiceState((prev) => (prev === 'speaking' ? 'idle' : prev));
    });
    return () => {
      audioElementRef.current?.pause();
      audioElementRef.current = null;
      window.speechSynthesis?.cancel();
      recognitionRef.current?.abort();
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
    };
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    audioElementRef.current?.pause();
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = 0;
    }
    setVoiceState((prev) => (prev === 'speaking' ? 'idle' : prev));
  }, []);

  const stopRecording = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.stop();
      return;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => !prev);
    if (voiceState === 'recording') {
      stopRecording();
    }
    if (voiceState === 'speaking') {
      stopSpeaking();
    }
  }, [voiceState, stopRecording, stopSpeaking]);

  const startWhisperRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const mimeType = pickRecorderMimeType();
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    audioChunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      const recordedType = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(audioChunksRef.current, { type: recordedType });
      if (blob.size < 100) {
        setVoiceState('idle');
        return;
      }

      setVoiceState('transcribing');

      try {
        const base64 = await blobToBase64(blob);
        const result = await stt({
          audio: base64,
          'content-type': recordedType.split(';')[0],
        }).unwrap();

        if (result.text) {
          onTranscriptRef.current?.(result.text);
        }
      } catch (err) {
        setError('Transcription failed. Please try again.');
        console.error('STT error:', err);
      } finally {
        setVoiceState('idle');
      }
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setVoiceState('recording');
  }, [stt]);

  const startBrowserRecording = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return false;

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-US';
    const parts: string[] = [];

    rec.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal && result[0]?.transcript) {
          parts.push(result[0].transcript.trim());
        }
      }
    };

    rec.onerror = (event) => {
      if (event.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone permission.');
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError('Transcription failed. Please try again.');
        console.error('Speech recognition error:', event.error);
      }
    };

    rec.onend = () => {
      recognitionRef.current = null;
      setVoiceState('idle');
      const text = parts.filter(Boolean).join(' ').trim();
      if (text) {
        onTranscriptRef.current?.(text);
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch {
      recognitionRef.current = null;
      return false;
    }
    setVoiceState('recording');
    return true;
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setVoiceEnabled(true);
    stopSpeaking();
    try {
      if (startBrowserRecording()) {
        return;
      }
      await startWhisperRecording();
    } catch (err) {
      setError('Microphone access denied. Please allow microphone permission.');
      console.error('Microphone error:', err);
    }
  }, [startBrowserRecording, startWhisperRecording, stopSpeaking]);

  const speakWithOpenAi = useCallback(
    async (text: string) => {
      setVoiceState('speaking');
      try {
        const result = await tts({ text, voice, model }).unwrap();
        if (result.audio && audioElementRef.current) {
          const audioBlob = base64ToBlob(result.audio, 'audio/mpeg');
          const url = URL.createObjectURL(audioBlob);
          audioElementRef.current.src = url;
          audioElementRef.current.play().catch(() => {
            setVoiceState('idle');
          });
        } else {
          setVoiceState('idle');
        }
      } catch (err) {
        console.error('TTS error:', err);
        setError('Speech playback failed. Please try again.');
        setVoiceState('idle');
      }
    },
    [tts, voice, model],
  );

  const speak = useCallback(
    async (text: string) => {
      if (!voiceEnabled || !autoSpeak || !text.trim()) return;

      window.speechSynthesis?.cancel();
      audioElementRef.current?.pause();

      if (canUseBrowserTts()) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.05;
        utterance.onend = () => {
          setVoiceState((prev) => (prev === 'speaking' ? 'idle' : prev));
        };
        utterance.onerror = () => {
          void speakWithOpenAi(text);
        };
        setVoiceState('speaking');
        window.speechSynthesis.speak(utterance);
        return;
      }

      await speakWithOpenAi(text);
    },
    [voiceEnabled, autoSpeak, speakWithOpenAi],
  );

  return {
    voiceEnabled,
    voiceState,
    error,
    toggleVoice,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  };
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

function canUseBrowserTts(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.speechSynthesis?.speak === 'function' &&
    typeof window.SpeechSynthesisUtterance === 'function'
  );
}

function pickRecorderMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(b64: string, contentType: string): Blob {
  const bytes = atob(b64);
  const array = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    array[i] = bytes.charCodeAt(i);
  }
  return new Blob([array], { type: contentType });
}
